import {
  forwardRef,
  HStack,
  Link,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router";

import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import useFeatureIntegrations from "@/feature/hooks/useFeatureIntegrations";
import { TargetingNotificationDefaultsInfo } from "@/feature/utils/targetingNotificationDefaults";
import { LinearTrackingEntityToggle } from "@/integration/components/linear/LinearTrackingEntityToggle";
import { SlackChannelToggle } from "@/integration/components/slack/SlackChannelToggle";
import { NotificationProvider } from "@reflag/shared/notification";
import {
  FeatureSettingsUrl,
  GlobalSettingsUrl,
  UrlArgs,
} from "@reflag/shared/urls";

type FeatureTargetingUpdateNotificationCalloutProps = StackProps & {
  feature: UrlArgs;
  value: NotificationProvider[] | undefined;
  onChange: (providers: NotificationProvider[]) => void;
  defaultInfo?: TargetingNotificationDefaultsInfo;
};

export const FeatureTargetingUpdateNotificationCallout = forwardRef(
  (
    {
      feature,
      value,
      onChange,
      defaultInfo,
      ...stackProps
    }: FeatureTargetingUpdateNotificationCalloutProps,
    ref,
  ) => {
    const { slack, linear } = useFeatureIntegrations(feature.id);

    const { env } = useEnvironment();

    const atLeastOneIntegration = slack || linear;
    const enabledProviders = useMemo(() => {
      return value ?? [];
    }, [value]);

    const handleToggleChange = (
      provider: NotificationProvider,
      enabled: boolean,
    ) => {
      onChange(
        enabled
          ? [...enabledProviders, provider]
          : enabledProviders.filter((p) => p !== provider),
      );
    };

    return atLeastOneIntegration ? (
      <VStack
        ref={ref}
        align="flex-start"
        alignSelf="flex-start"
        spacing={1}
        {...stackProps}
      >
        <HStack spacing={1}>
          <Text color="dimmed">Post update to</Text>
          {defaultInfo && (
            <InfoIconTooltip
              _content={{ maxW: "sm" }}
              placement="right"
              text={
                <VStack align="flex-start" spacing={1}>
                  <Text>
                    Defaults come from{" "}
                    <Link
                      as={RouterLink}
                      to={GlobalSettingsUrl(env, "app-notifications")}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Notification settings
                    </Link>
                    .
                  </Text>
                  <Text>
                    You can override these toggles for this update only.
                  </Text>
                </VStack>
              }
            />
          )}
        </HStack>
        {slack && (
          <SlackChannelToggle
            enabled={enabledProviders.includes("slack")}
            slackChannel={slack.slackChannel}
            onToggle={(enabled) => handleToggleChange("slack", enabled)}
          />
        )}

        {linear && (
          <LinearTrackingEntityToggle
            enabled={enabledProviders.includes("linearComment")}
            entity={linear.trackingEntity}
            showTypeIcon={false}
            onToggle={(enabled) => handleToggleChange("linearComment", enabled)}
          />
        )}
      </VStack>
    ) : (
      feature && (
        <Text alignSelf="flex-start" color="dimmed">
          Updates can be posted to{" "}
          <Link as={RouterLink} to={FeatureSettingsUrl(env, feature, "slack")}>
            Slack
          </Link>{" "}
          or{" "}
          <Link as={RouterLink} to={FeatureSettingsUrl(env, feature, "linear")}>
            Linear
          </Link>
          .
        </Text>
      )
    );
  },
);

FeatureTargetingUpdateNotificationCallout.displayName =
  "FeatureTargetingUpdateNotificationCallout";
